'use client';

import React, { useState } from 'react';
import FormContent from './FormContent';
import SuccessMessage from './SuccessMessage';
import { trackClick } from '@/utils/trackingUtils';

interface QuoteFormProps {
  formTitle: string;
  phoneNumber: string;
  isHighlighted: boolean;
}

const QuoteForm: React.FC<QuoteFormProps> = ({
  formTitle,
  phoneNumber,
  isHighlighted
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    equipment: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    duration: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEquipmentChange = (equipmentType: string) => {
    setFormData(prev => ({
      ...prev,
      equipment: equipmentType
    }));
  };

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    setFormData(prev => {
      const updatedData = { ...prev, [field]: date };
      
      // Auto-calculate duration when both dates are set
      if (updatedData.startDate && updatedData.endDate) {
        const start = updatedData.startDate;
        const end = updatedData.endDate;
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        updatedData.duration = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      } else {
        updatedData.duration = '';
      }
      
      return updatedData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          subject: `Equipment Hire Quote Request - ${formData.equipment || 'Multiple Items'}`,
          message: `
Equipment: ${formData.equipment || 'None selected'}
Start Date: ${formData.startDate ? formData.startDate.toLocaleDateString() : 'Not specified'}
End Date: ${formData.endDate ? formData.endDate.toLocaleDateString() : 'Not specified'}
Duration: ${formData.duration}
Phone: ${formData.phone}
Email: ${formData.email}

Additional Details:
${formData.message || 'None provided'}

---
This is a quote request from the equipment hire landing page.
          `.trim(),
          type: 'quote'
        }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          equipment: '',
          startDate: null,
          endDate: null,
          duration: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`Failed to submit quote request: ${JSON.stringify(errorData)}`);
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Sorry, there was an error submitting your quote request. Please try calling us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallNowClick = (e: React.MouseEvent) => {
    // Track the click before the call
    trackClick('call_now', 'hero-call-button');
    
    // Let the default phone action proceed
    return true;
  };

  return (
    <div 
      id="quote-form"
      className={`backdrop-blur-xl bg-white/95 border border-white/20 rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl transition-all duration-500 ${
        isHighlighted 
          ? 'ring-4 ring-yellow-400 ring-opacity-75 animate-pulse shadow-yellow-400/50' 
          : ''
      } ${
        isHighlighted ? 'animate-shake' : ''
      }`}
    >
      {!submitSuccess ? (
        <FormContent
          formData={formData}
          handleInputChange={handleInputChange}
          handleEquipmentChange={handleEquipmentChange}
          handleDateChange={handleDateChange}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          formTitle={formTitle}
        />
      ) : (
        <SuccessMessage
          phoneNumber={phoneNumber}
          handleCallNowClick={handleCallNowClick}
        />
      )}
    </div>
  );
};

export default QuoteForm;